import type { TFunction } from 'i18next';

export const getOrderProductSaveErrorMessage = (_t: TFunction, _error: unknown, fallback: string) => {
  // API `message` / `error` values are diagnostic prose (often English), not
  // a stable Polish UI contract. Known actionable codes have their own mapper
  // below; all other save failures use the caller's precise Polish fallback.
  return fallback;
};

export interface OrderProductDeleteCargoConflictResponse {
  code?: unknown;
  routeNumbers?: unknown;
  productNames?: unknown;
}

const toStringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

// S32 (order-product delete guard): PUT /orders/:id/products answers 400
// ORDER_PRODUCT_HAS_TRANSPORT_CARGO when the payload would delete a line that
// is still loaded on a transport (see orderProductsService
// .assertDeletableWithoutOrphaningCargo). The backend's `message` is an
// English sentence meant for logs/API consumers — map the stable `code` to
// Polish-first copy here instead of echoing it, same pattern as
// transportRouteViews.mapTransportMutationErrorToMessage. Returns undefined
// for any other error so callers fall through to
// getOrderProductSaveErrorMessage's generic message/fallback handling.
export const getOrderProductDeleteCargoConflictMessage = (
  t: TFunction,
  error: unknown,
): string | undefined => {
  const responseData = (error as { response?: { data?: OrderProductDeleteCargoConflictResponse } })
    ?.response?.data;

  if (responseData?.code !== 'ORDER_PRODUCT_HAS_TRANSPORT_CARGO') {
    return undefined;
  }

  return t('orders:orderDetail.messages.productHasTransportCargoError', {
    products: toStringList(responseData.productNames).join(', '),
    routes: toStringList(responseData.routeNumbers).join(', '),
  });
};
